import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaPlus, FaEdit, FaHistory, FaSpinner, FaTimes, FaSave } from 'react-icons/fa';
import Button from '../../components/Button';
import Table from '../../components/Table';
import Input from '../../components/Input';
import PageHeader from '../../components/PageHeader';
import Skeleton from '../../components/Skeleton';
import { modelPricingService } from '../../services/modelPricingService';
import { toastSuccess, toastError } from '../../utils/toast';

const fmtPrice = (v) => v == null ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
const fmtDate = (s) => {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const EMPTY_FORM = { model: '', price_per_1m_prompt: '', price_per_1m_completion: '' };

const cardStyle = {
  background: 'linear-gradient(160deg, #161731 0%, #12132a 55%, #0f1024 100%)',
  border: '1px solid rgba(255,255,255,0.07)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.35)',
  borderRadius: '12px',
  padding: '24px',
};

const PriceModal = ({ isOpen, mode, currentModel, onClose, onSaved }) => {
  // mode: 'new' (modelo nuevo, nombre editable) | 'update' (precio nuevo de modelo existente)
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      model: mode === 'update' ? currentModel?.model || '' : '',
      price_per_1m_prompt: '',
      price_per_1m_completion: '',
    });
    setErrors({});
  }, [isOpen, mode, currentModel]);

  const validate = () => {
    const e = {};
    if (!form.model.trim()) e.model = 'Requerido';
    if (form.price_per_1m_prompt === '' || isNaN(form.price_per_1m_prompt) || Number(form.price_per_1m_prompt) < 0)
      e.price_per_1m_prompt = 'Número válido requerido';
    if (form.price_per_1m_completion === '' || isNaN(form.price_per_1m_completion) || Number(form.price_per_1m_completion) < 0)
      e.price_per_1m_completion = 'Número válido requerido';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await modelPricingService.addPrice({
        model: form.model.trim(),
        price_per_1m_prompt: Number(form.price_per_1m_prompt),
        price_per_1m_completion: Number(form.price_per_1m_completion),
      });
      toastSuccess(mode === 'new' ? 'Modelo agregado' : 'Precio actualizado');
      onSaved();
      onClose();
    } catch (err) {
      toastError(err.message || 'Error al guardar precio');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
      <div className="relative bg-surface-card border border-white/[0.06] rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-5 border-b border-white/[0.07]">
          <h2 className="font-semibold" style={{ color: '#E8EAF0' }}>
            {mode === 'new' ? 'Nuevo modelo' : `Actualizar precio — ${currentModel?.model}`}
          </h2>
          <button onClick={onClose} style={{ color: '#4A5270' }} className="hover:text-white transition-colors"><FaTimes size={16} /></button>
        </div>
        <div className="p-5 space-y-2">
          {mode === 'new' ? (
            <Input
              label="Nombre del modelo *"
              placeholder="Ej: gpt-4o-mini"
              value={form.model}
              onChange={e => setForm(p => ({ ...p, model: e.target.value }))}
              error={errors.model}
            />
          ) : (
            <p className="text-xs mb-3" style={{ color: '#6B7391' }}>
              Esto crea un nuevo registro de precio; el precio vigente actual se cierra automáticamente y el costo ya calculado en conversaciones pasadas no se ve afectado.
            </p>
          )}
          <Input
            label="Precio / 1M tokens prompt (USD) *"
            type="number"
            min="0"
            step="0.0001"
            placeholder="Ej: 0.15"
            value={form.price_per_1m_prompt}
            onChange={e => setForm(p => ({ ...p, price_per_1m_prompt: e.target.value }))}
            error={errors.price_per_1m_prompt}
          />
          <Input
            label="Precio / 1M tokens completion (USD) *"
            type="number"
            min="0"
            step="0.0001"
            placeholder="Ej: 0.60"
            value={form.price_per_1m_completion}
            onChange={e => setForm(p => ({ ...p, price_per_1m_completion: e.target.value }))}
            error={errors.price_per_1m_completion}
          />
        </div>
        <div className="flex justify-end gap-3 p-5 border-t border-white/[0.07]">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? <FaSpinner className="animate-spin mr-2" /> : <FaSave className="mr-2" />}
            Guardar
          </Button>
        </div>
      </div>
    </div>
  );
};

const HistoryModal = ({ isOpen, model, onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !model) return;
    setLoading(true);
    modelPricingService.getHistory(model)
      .then(setRows)
      .catch(err => toastError(err.message || 'Error al cargar histórico'))
      .finally(() => setLoading(false));
  }, [isOpen, model]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
      <div className="relative bg-surface-card border border-white/[0.06] rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/[0.07]">
          <h2 className="font-semibold" style={{ color: '#E8EAF0' }}>Histórico — {model}</h2>
          <button onClick={onClose} style={{ color: '#4A5270' }} className="hover:text-white transition-colors"><FaTimes size={16} /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-2">
          {loading ? (
            <Skeleton.TableRows rows={4} cols={3} />
          ) : rows.length === 0 ? (
            <p className="text-xs" style={{ color: '#4A5270' }}>Sin histórico.</p>
          ) : (
            rows.map(r => (
              <div key={r.id} className="flex items-center justify-between py-2 px-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div>
                  <p className="text-xs font-mono" style={{ color: '#C8CDD9' }}>
                    prompt {fmtPrice(r.price_per_1m_prompt)} · completion {fmtPrice(r.price_per_1m_completion)}
                  </p>
                  <p className="text-[10px]" style={{ color: '#4A5270' }}>
                    {fmtDate(r.valid_from)} → {r.valid_to ? fmtDate(r.valid_to) : 'vigente'}
                  </p>
                </div>
                {!r.valid_to && (
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(34,197,94,0.12)', color: '#4ADE80' }}>Vigente</span>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const AdminPreciosPage = () => {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedModel, setSelectedModel] = useState(null);
  const [historyModel, setHistoryModel] = useState(null);

  const fetchPrices = async () => {
    setLoading(true);
    try {
      const data = await modelPricingService.getCurrent();
      setPrices(data);
    } catch (err) {
      toastError(err.message || 'Error al cargar precios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPrices(); }, []);

  const handleUpdatePrice = (row) => {
    setModalMode('update');
    setSelectedModel(row);
    setModalOpen(true);
  };

  const handleNewModel = () => {
    setModalMode('new');
    setSelectedModel(null);
    setModalOpen(true);
  };

  const columns = [
    {
      key: 'model',
      title: 'Modelo',
      sortable: true,
      render: (v) => <span className="font-medium font-mono text-sm" style={{ color: '#E8EAF0' }}>{v}</span>,
    },
    {
      key: 'price_per_1m_prompt',
      title: 'Prompt / 1M',
      sortable: true,
      render: (v) => <span className="text-sm font-mono" style={{ color: '#60A5FA' }}>{fmtPrice(v)}</span>,
    },
    {
      key: 'price_per_1m_completion',
      title: 'Completion / 1M',
      sortable: true,
      render: (v) => <span className="text-sm font-mono" style={{ color: '#A78BFA' }}>{fmtPrice(v)}</span>,
    },
    {
      key: 'valid_from',
      title: 'Vigente desde',
      sortable: true,
      render: (v) => <span className="text-xs" style={{ color: '#6B7391' }}>{fmtDate(v)}</span>,
    },
    {
      key: 'actions',
      title: '',
      sortable: false,
      render: (_, row) => (
        <div className="flex gap-2 justify-end">
          <button
            onClick={() => setHistoryModel(row.model)}
            className="p-1.5 rounded-lg hover:bg-white/5 transition-colors" style={{ color: '#6B7391' }}
            title="Ver histórico"
          >
            <FaHistory size={14} />
          </button>
          <button
            onClick={() => handleUpdatePrice(row)}
            className="p-1.5 rounded-lg text-blue-400 hover:bg-blue-500/10 transition-colors"
            title="Actualizar precio"
          >
            <FaEdit size={14} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="Precios de modelos" subtitle="Catálogo de precios OpenAI usados para calcular el costo por conversación">
        <Button variant="warning" onClick={handleNewModel} leftIcon={<FaPlus size={11} />}>
          Agregar modelo nuevo
        </Button>
      </PageHeader>

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        style={cardStyle}
      >
        {loading ? (
          <Skeleton.TableRows rows={5} cols={5} />
        ) : (
          <Table
            data={prices}
            columns={columns}
            options={{
              pagination: false,
              showSearch: false,
              emptyMessage: 'No hay modelos con precio configurado',
            }}
          />
        )}
      </motion.div>

      <PriceModal
        isOpen={modalOpen}
        mode={modalMode}
        currentModel={selectedModel}
        onClose={() => setModalOpen(false)}
        onSaved={fetchPrices}
      />
      <HistoryModal
        isOpen={!!historyModel}
        model={historyModel}
        onClose={() => setHistoryModel(null)}
      />
    </div>
  );
};

export default AdminPreciosPage;
